let timelineInstances = [];

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function setupTimeline(timeline) {
  const progress = timeline.querySelector('[data-about-timeline-progress]');
  const axis = timeline.querySelector('.crms-about-timeline__axis');
  const items = Array.from(timeline.querySelectorAll('[data-about-timeline-item]'));

  if (!progress || !items.length) {
    return { destroy() {} };
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    progress.style.transform = 'scaleY(1)';
    if (axis) {
      axis.style.setProperty('--timeline-progress', '1');
      axis.style.setProperty('--timeline-head-opacity', '0');
    }

    items.forEach((item) => {
      item.classList.add('is-visible');
      item.classList.remove('is-active');
    });

    return { destroy() {} };
  }

  items.forEach((item, index) => {
    item.style.setProperty('--timeline-delay', `${Math.min(index, 6) * 75}ms`);
  });

  let observer;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    items.forEach((item) => observer.observe(item));
  } else {
    items.forEach((item) => item.classList.add('is-visible'));
  }

  let ticking = false;
  const updateItemState = (viewport) => {
    const viewportCenter = viewport * 0.5;
    const viewportRange = Math.max(viewport * 0.7, 320);

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.top + rect.height * 0.5;
      const normalizedDistance = clamp((itemCenter - viewportCenter) / viewportRange, -1, 1);
      const focus = 1 - Math.min(Math.abs(normalizedDistance), 1);
      const direction = index % 2 === 0 ? -1 : 1;
      const drift = Math.abs(normalizedDistance);
      const xShift = direction * drift * 12;
      const tilt = direction * drift * 0.55;
      const inViewportBand = rect.bottom > viewport * 0.12 && rect.top < viewport * 0.88;

      item.style.setProperty('--timeline-item-shift', `${xShift.toFixed(2)}px`);
      item.style.setProperty('--timeline-item-tilt', `${tilt.toFixed(2)}deg`);
      item.style.setProperty('--timeline-item-focus', focus.toFixed(3));

      if (focus > 0.74 && inViewportBand) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
  };

  const updateProgress = () => {
    const rect = timeline.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const totalDistance = rect.height + viewport * 0.3;
    const traveled = viewport * 0.74 - rect.top;
    const ratio = clamp(traveled / totalDistance, 0, 1);

    progress.style.transform = `scaleY(${ratio})`;
    if (axis) {
      axis.style.setProperty('--timeline-progress', ratio.toFixed(4));
      axis.style.setProperty('--timeline-head-opacity', ratio > 0.02 && ratio < 0.995 ? '1' : '0');
    }

    updateItemState(viewport);
  };

  const onScroll = () => {
    if (ticking) {
      return;
    }

    ticking = true;
    requestAnimationFrame(() => {
      updateProgress();
      ticking = false;
    });
  };

  updateProgress();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  return {
    destroy() {
      if (observer) {
        observer.disconnect();
      }

      items.forEach((item) => {
        item.classList.remove('is-visible', 'is-active');
        item.style.removeProperty('--timeline-delay');
        item.style.removeProperty('--timeline-item-shift');
        item.style.removeProperty('--timeline-item-tilt');
        item.style.removeProperty('--timeline-item-focus');
      });

      progress.style.removeProperty('transform');
      if (axis) {
        axis.style.removeProperty('--timeline-progress');
        axis.style.removeProperty('--timeline-head-opacity');
      }

      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    },
  };
}

function destroyAboutTimeline() {
  timelineInstances.forEach((instance) => instance.destroy());
  timelineInstances = [];
}

function initAboutTimeline() {
  destroyAboutTimeline();

  const timelines = Array.from(document.querySelectorAll('[data-about-timeline]'));
  if (!timelines.length) {
    return;
  }

  timelineInstances = timelines.map((timeline) => setupTimeline(timeline));
}

initAboutTimeline();
document.addEventListener('turbo:load', initAboutTimeline);
document.addEventListener('turbo:before-cache', destroyAboutTimeline);
